import { FC, useCallback, useMemo } from 'react';
import DialogContentText from '@material-ui/core/DialogContentText';
import { Dialog } from './Dialog';
import { IActionsProps, IActionVariant } from './ButtonPanel';
import classNames from 'classnames';
import { getIsMobile } from 'config/PortalConfiguration';

export type AlertType = 'Error' | 'Warning' | 'Information' | 'Question';

export interface IAlertDialogProps {
  open: boolean;
  messages: string[];
  onClose: () => void;
  /**
   * Because this got tied to Decisions.MessageDialog, which conflates the idea of an alert and a modal,
   * even though this is named "alert" it is also used to show other icons
   */
  actionHandler?: (name: string) => void;
  actionName?: string | string[];
  title?: string;
  actionVariant?: IActionVariant;
  type: AlertType;
  /** `true` to replicate the behavior top-pinned error/warning dialogs */
  pinnedToTop?: boolean;
  forMobile?: boolean;
}

export const AlertDialog: FC<IAlertDialogProps> = ({
  messages,
  onClose,
  actionHandler,
  actionName = 'Dismiss',
  actionVariant,
  title,
  open,
  pinnedToTop,
  type,
}) => {
  const isMobile = getIsMobile();
  const contentView = useMemo(() => {
    return messages.map((warning, ind) => {
      return (
        <DialogContentText className="dp-alert-dialog__text" key={ind} id={ind.toString()}>
          {warning}
        </DialogContentText>
      );
    });
  }, [messages]);

  const onActionClick = useCallback(
    (actionName: string) => {
      actionHandler?.(actionName);
      onClose();
    },
    [actionHandler, onClose]
  );

  const actions: IActionsProps[] = useMemo(() => {
    if (Array.isArray(actionName)) {
      return actionName.map(act => ({
        name: act,
        onClick: () => onActionClick(act),
      }));
    }

    return [{ name: actionName, onClick: () => onActionClick(actionName) }];
  }, [actionName, onActionClick]);

  const iconClasses = classNames({
    'dp-warning-dialog__icon': type === 'Warning',
    'dp-error-dialog__icon': type === 'Error',
    'dp-info-dialog__icon': type === 'Information',
    'dp-question-dialog__icon': type === 'Question',
  });

  const actionsClassName = classNames({ 'dp-alert-dialog__actions--mobile': isMobile });

  const dialogPosition = pinnedToTop
    ? {
        top: 0,
      }
    : undefined;

  const margin = pinnedToTop ? 0 : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      hideTitle={pinnedToTop || (isMobile && !title?.trim())}
      dialogTitle={{ titleText: title }}
      dialogActions={{
        actions,
        // mobile designs all use primary for now:
        variant: isMobile ? actionVariant || 'primary' : actionVariant,
        className: actionsClassName,
      }}
      styles={{ minWidth: 350, margin }}
      disableFullScreenAction
      isResponsive
      className={classNames('dp-alert-dialog', {
        'dp-alert-dialog--pinned-to-top': pinnedToTop,
      })}
      contentClassName={classNames('dp-alert-dialog__content-holder', {
        'dp-alert-dialog__content-holder--mobile': isMobile,
      })}
      dialogPosition={dialogPosition}
    >
      <i className={iconClasses} aria-label={`${type} icon`}></i>
      <div className="dp-alert-dialog__content"> {contentView} </div>
    </Dialog>
  );
};
